import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './ProjectEdit.css';
import { buildApiUrl } from '../config/api';
import { 
  ACTIVITY_CALENDAR_COLORS, 
  getActivityColor, 
  isToday, 
  formatActivityDate, 
  WEEK_DAYS, 
  MONTHS, 
  getMonth, 
  groupByWeekMondayStart,
  getCalendarDayStyle,
  calculateMonthLabelsByFirstMonday
} from './ActivityCalendar';

interface UserProject {
  id: number;
  user_project_id: number;
  percentage: number;
  hours: number;
  target_hours: number;
  change_percentage: number;
  change_type: string;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  project_id: number;
}

interface ProjectInfo {
  id: number;
  name: string;
  category: string;
  description: string;
  icon: string;
  owner_type: string;
}

interface ActivityRecord {
  id: number;
  user_id: number;
  project_id: number;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  created_at: string;
  updated_at: string;
}

// 新增：活动日历数据接口
interface ActivityDay {
  date: string; // YYYY-MM-DD
  hours: number;
}

interface ProjectEditProps {
  userProjectId: number;
  onBack: () => void;
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>;
  onProjectInfoUpdated?: () => void;
  currentTheme?: string;
}

// 新增：数据获取状态管理
interface DataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const ProjectEdit: React.FC<ProjectEditProps> = ({ userProjectId, onBack, authenticatedFetch, onProjectInfoUpdated, currentTheme = 'modern' }) => {
  // 基础数据状态 - 使用独立的状态管理
  const [userProject, setUserProject] = useState<DataState<UserProject>>({
    data: null,
    loading: true,
    error: null
  });
  
  const [projectInfo, setProjectInfo] = useState<DataState<ProjectInfo>>({
    data: null,
    loading: true,
    error: null
  });
  
  const [activityRecords, setActivityRecords] = useState<DataState<ActivityRecord[]>>({
    data: [],
    loading: true,
    error: null
  });
  
  const [calendarData, setCalendarData] = useState<DataState<ActivityDay[]>>({
    data: [],
    loading: true,
    error: null
  });
  
  const [weeklyAnalysis, setWeeklyAnalysis] = useState<DataState<{
    analysis: string;
    summary: string;
    recommendations: string[];
    timestamp: string;
  }>>({
    data: null,
    loading: true,
    error: null
  });

  // 编辑相关状态
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editData, setEditData] = useState({
    target_hours: ''
  });
  
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [hasMoreRecords, setHasMoreRecords] = useState(true);
  const [loadingMoreRecords, setLoadingMoreRecords] = useState(false);
  
  // 编辑活动记录相关状态
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
  const [editingStartTime, setEditingStartTime] = useState('');
  const [editingEndTime, setEditingEndTime] = useState('');
  const [updatingRecord, setUpdatingRecord] = useState(false);
  
  // 活动日历相关状态
  const [calendarMax, setCalendarMax] = useState(1);
  const [calendarWeeks, setCalendarWeeks] = useState<ActivityDay[][]>([]);
  const [monthLabels, setMonthLabels] = useState<string[]>([]);
  const calendarScrollRef = useRef<HTMLDivElement>(null);
  const recordsScrollRef = useRef<HTMLDivElement>(null);
  
  // 新增：toast相关状态
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // 防止重复请求的ref
  const fetchingRefs = useRef({
    userProject: false,
    projectInfo: false,
    activityRecords: false,
    calendarData: false,
    weeklyAnalysis: false
  });

  // 左滑回到首页相关状态
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 动态获取app-header高度
  const [headerHeight, setHeaderHeight] = useState(51.2);

  // 检测是否为暗色主题
  const isDarkTheme = currentTheme === 'dark';

  // 新增：显示toast的通用函数
  const showToast = useCallback((message: string, duration: number = 3000) => {
    // 清除之前的定时器
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // 设置新的toast
    setToast(message);
    
    // 设置自动隐藏定时器
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  // 新增：清除toast的函数
  const clearToast = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // 左滑回到首页事件处理
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
    setSwipeX(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - touchStartX.current;
    const deltaY = Math.abs(currentY - touchStartY.current);
    
    // 确保是水平滑动（垂直滑动距离小于水平滑动距离的一半）
    if (deltaY < Math.abs(deltaX) * 0.5) {
      // 只允许向右滑动（左滑回到首页）
      if (deltaX > 0) {
        const maxSwipe = 100; // 最大滑动距离
        const swipeDistance = Math.min(deltaX, maxSwipe);
        setSwipeX(swipeDistance);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    
    // 如果滑动距离超过50px，触发回到首页
    if (swipeX > 50) {
      onBack();
    } else {
      // 否则重置滑动状态
      setSwipeX(0);
    }
    
    setIsSwiping(false);
  };

  // 获取app-header的实际高度
  const getAppHeaderHeight = useCallback(() => {
    const appHeader = document.querySelector('.app-header');
    if (appHeader) {
      const height = appHeader.getBoundingClientRect().height;
      setHeaderHeight(height);
      return height;
    }
    return 51.2; // 默认值
  }, []);

  // 处理日历格子点击
  const handleDayClick = (day: ActivityDay, event: React.MouseEvent | React.TouchEvent) => {
    console.log('=== 日历格子点击事件触发 ===');
    console.log('事件类型:', event.type);
    console.log('点击的日期:', day.date);
    console.log('点击的时长:', day.hours);
    
    // 移除 preventDefault() 以避免被动事件监听器错误
    // 只使用 stopPropagation() 阻止事件冒泡
    event.stopPropagation();
    
    if (day.date) {
      console.log('显示toast提示');
      
      // 显示toast提示工作时长
      showToast(`${formatActivityDate(day.date)} - 工作时长: ${day.hours}小时`, 3000);
    } else {
      console.log('没有日期，不显示提示');
    }
  };

  // ========== 独立的数据获取函数 ==========
  
  // 1. 获取用户项目详情 - 独立获取
  const fetchUserProject = useCallback(async () => {
    if (fetchingRefs.current.userProject) return;
    fetchingRefs.current.userProject = true;
    
    console.log('🚀 开始获取用户项目详情...');
    setUserProject(prev => ({ ...prev, loading: true, error: null }));
    
    // 设置10秒超时
    const timeoutId = setTimeout(() => {
      console.log('⏰ 用户项目详情请求超时，渲染默认内容');
      setUserProject(prev => ({ ...prev, loading: false, error: '请求超时' }));
      fetchingRefs.current.userProject = false;
    }, 10000);
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const url = authenticatedFetch ? `/api/me/projects/${userProjectId}` : buildApiUrl(`/api/me/projects/${userProjectId}`);
      
      const response = await fetchFunction(url);
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 用户项目详情获取成功:', data);
        setUserProject({ data, loading: false, error: null });
        setEditData({ target_hours: data.target_hours.toString() });
      } else {
        console.error('❌ 获取用户项目详情失败:', response.status);
        setUserProject(prev => ({ ...prev, loading: false, error: '获取用户项目详情失败' }));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('获取用户项目详情失败:', error);
      setUserProject(prev => ({ ...prev, loading: false, error: '网络错误' }));
    } finally {
      fetchingRefs.current.userProject = false;
    }
  }, [userProjectId, authenticatedFetch]);

  // 2. 获取项目基本信息 - 独立获取
  const fetchProjectInfo = useCallback(async (projectId: number) => {
    if (fetchingRefs.current.projectInfo) return;
    fetchingRefs.current.projectInfo = true;
    
    console.log('🚀 开始获取项目基本信息...');
    setProjectInfo(prev => ({ ...prev, loading: true, error: null }));
    
    // 设置10秒超时
    const timeoutId = setTimeout(() => {
      console.log('⏰ 项目基本信息请求超时，渲染默认内容');
      setProjectInfo(prev => ({ ...prev, loading: false, error: '请求超时' }));
      fetchingRefs.current.projectInfo = false;
    }, 10000);
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const url = authenticatedFetch ? `/api/projects/${projectId}` : buildApiUrl(`/api/projects/${projectId}`);
      
      const response = await fetchFunction(url);
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 项目基本信息获取成功:', data);
        setProjectInfo({ data, loading: false, error: null });
      } else {
        console.error('❌ 获取项目基本信息失败:', response.status);
        setProjectInfo(prev => ({ ...prev, loading: false, error: '获取项目基本信息失败' }));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('获取项目基本信息失败:', error);
      setProjectInfo(prev => ({ ...prev, loading: false, error: '网络错误' }));
    } finally {
      fetchingRefs.current.projectInfo = false;
    }
  }, [authenticatedFetch]);

  // 3. 获取活动记录 - 独立获取
  const fetchActivityRecords = useCallback(async (projectId: number, page: number = 1, append: boolean = false) => {
    if (fetchingRefs.current.activityRecords) return;
    fetchingRefs.current.activityRecords = true;
    
    console.log('🚀 开始获取活动记录...');
    setActivityRecords(prev => ({ ...prev, loading: true, error: null }));
    
    // 设置10秒超时
    const timeoutId = setTimeout(() => {
      console.log('⏰ 活动记录请求超时，渲染默认内容');
      setActivityRecords(prev => ({ ...prev, loading: false, error: '请求超时' }));
      fetchingRefs.current.activityRecords = false;
    }, 10000);
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const url = authenticatedFetch 
        ? `/api/me/activities?project_id=${projectId}&page=${page}&page_size=${pageSize}` 
        : buildApiUrl(`/api/me/activities?project_id=${projectId}&page=${page}&page_size=${pageSize}`);
      
      const response = await fetchFunction(url);
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 活动记录获取成功:', data);
        
        const records = data.records || data;
        setActivityRecords(prev => ({ 
          data: append ? [...(prev.data || []), ...records] : records, 
          loading: false, 
          error: null 
        }));
        
        if (data.records && data.records.length < pageSize) {
          setHasMoreRecords(false);
        }
        setCurrentPage(page);
      } else {
        console.error('❌ 获取活动记录失败:', response.status);
        setActivityRecords(prev => ({ ...prev, loading: false, error: '获取活动记录失败' }));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('获取活动记录失败:', error);
      setActivityRecords(prev => ({ ...prev, loading: false, error: '网络错误' }));
    } finally {
      fetchingRefs.current.activityRecords = false;
    }
  }, [authenticatedFetch, pageSize]);

  // 4. 获取活动日历数据 - 独立获取
  const fetchCalendarData = useCallback(async () => {
    if (fetchingRefs.current.calendarData) return;
    fetchingRefs.current.calendarData = true;
    
    console.log('🚀 开始获取活动日历数据...');
    setCalendarData(prev => ({ ...prev, loading: true, error: null }));
    
    // 设置10秒超时
    const timeoutId = setTimeout(() => {
      console.log('⏰ 活动日历数据请求超时，渲染默认内容');
      setCalendarData(prev => ({ ...prev, loading: false, error: '请求超时' }));
      fetchingRefs.current.calendarData = false;
    }, 10000);
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      
      // 计算最近6个月的时间范围
      const today = new Date();
      const endStr = today.toISOString().slice(0, 10);
      const startDate = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000); // 6个月
      const startStr = startDate.toISOString().slice(0, 10);
      
      const url = authenticatedFetch 
        ? `/api/me/projects/${userProjectId}/activity_calendar?start=${startStr}&end=${endStr}`
        : buildApiUrl(`/api/me/projects/${userProjectId}/activity_calendar?start=${startStr}&end=${endStr}`);
      
      const response = await fetchFunction(url);
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 活动日历数据获取成功:', data);
        setCalendarData({ data, loading: false, error: null });
        setCalendarMax(Math.max(1, ...data.map((d: ActivityDay) => d.hours)));
        
        const weekArr = groupByWeekMondayStart(data, startStr, endStr);
        setCalendarWeeks(weekArr);
        
        // 使用新的月份标签计算函数，让每个月对齐该月的第一个周一
        const labels = calculateMonthLabelsByFirstMonday(weekArr, startStr, endStr);
        setMonthLabels(labels);
      } else {
        console.error('❌ 获取活动日历失败:', response.status);
        setCalendarData(prev => ({ ...prev, loading: false, error: '获取活动日历失败' }));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('获取活动日历失败:', error);
      setCalendarData(prev => ({ ...prev, loading: false, error: '网络错误' }));
    } finally {
      fetchingRefs.current.calendarData = false;
    }
  }, [userProjectId, authenticatedFetch]);

  // 5. 获取周报分析 - 独立获取
  const fetchWeeklyAnalysis = useCallback(async () => {
    if (fetchingRefs.current.weeklyAnalysis) return;
    fetchingRefs.current.weeklyAnalysis = true;
    
    console.log('🚀 开始获取周报分析...');
    setWeeklyAnalysis(prev => ({ ...prev, loading: true, error: null }));
    
    // 设置1分钟超时
    const timeoutId = setTimeout(() => {
      console.log('⏰ 周报分析请求超时，渲染默认内容');
      setWeeklyAnalysis(prev => ({ ...prev, loading: false, error: '请求超时' }));
      fetchingRefs.current.weeklyAnalysis = false;
    }, 60000);
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const url = authenticatedFetch 
        ? `/api/ai/project-weekly-analysis`
        : buildApiUrl(`/api/ai/project-weekly-analysis`);
      
      const requestBody = {
        user_project_id: userProjectId
      };
      
      const response = await fetchFunction(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 周报分析获取成功:', data);
        setWeeklyAnalysis({ data, loading: false, error: null });
      } else {
        console.error('❌ 获取周报分析失败:', response.status);
        setWeeklyAnalysis(prev => ({ ...prev, loading: false, error: '获取周报分析失败' }));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('获取周报分析失败:', error);
      setWeeklyAnalysis(prev => ({ ...prev, loading: false, error: '网络错误' }));
    } finally {
      fetchingRefs.current.weeklyAnalysis = false;
    }
  }, [userProjectId, authenticatedFetch]);

  // ========== 组件初始化 ==========
  
  // 组件挂载时同时发起所有请求
  useEffect(() => {
    console.log('🎬 ProjectEdit组件挂载，同时发起所有数据请求...');
    
    // 同时发起所有请求，不相互等待
    fetchUserProject();
    fetchCalendarData(); // 可以直接发起，不需要等待
    fetchWeeklyAnalysis(); // 可以直接发起，不需要等待
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 当用户项目详情获取成功后，触发需要project_id的请求
  useEffect(() => {
    if (userProject.data && !userProject.loading && !userProject.error) {
      console.log('📋 用户项目详情已获取，开始获取其他数据...');
      
      // 获取需要project_id的数据
      fetchProjectInfo(userProject.data.project_id);
      fetchActivityRecords(userProject.data.project_id);
    }
  }, [userProject.data, userProject.loading, userProject.error]); // eslint-disable-line react-hooks/exhaustive-deps

  // 组件挂载时获取app-header高度
  useEffect(() => {
    // 延迟一点时间确保DOM已经渲染
    const timer = setTimeout(() => {
      getAppHeaderHeight();
    }, 100);
    
    // 监听窗口大小变化，重新获取app-header高度
    const handleResize = () => {
      getAppHeaderHeight();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [getAppHeaderHeight]);

  // 初始时自动滚动到最右侧
  useEffect(() => {
    if (calendarScrollRef.current && calendarWeeks.length > 0) {
      calendarScrollRef.current.scrollLeft = calendarScrollRef.current.scrollWidth;
    }
  }, [calendarWeeks, monthLabels]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const formatDuration = (minutes: number) => {
    // 四舍五入到整数分钟
    const roundedMinutes = Math.round(minutes);
    const hours = Math.floor(roundedMinutes / 60);
    const mins = roundedMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  // 开始编辑活动记录
  const handleStartEdit = (record: ActivityRecord) => {
    setEditingRecordId(record.id);
    
    // 将UTC时间转换为本地时间用于输入框
    const startDate = new Date(record.start_time);
    const endDate = new Date(record.end_time);
    
    // 格式化为本地时间的 datetime-local 格式 (YYYY-MM-DDTHH:mm)
    const formatLocalDateTime = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setEditingStartTime(formatLocalDateTime(startDate));
    setEditingEndTime(formatLocalDateTime(endDate));
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingRecordId(null);
    setEditingStartTime('');
    setEditingEndTime('');
  };

  // 保存编辑的活动记录
  const handleSaveEdit = async (recordId: number) => {
    if (!editingStartTime || !editingEndTime) {
      setMessage('请填写完整的时间信息');
      return;
    }

    // 将本地时间转换为UTC时间
    // datetime-local 输入框返回的是本地时间（东八区），我们需要将其转换为UTC
    const startTime = new Date(editingStartTime + ':00'); // 添加秒数
    const endTime = new Date(editingEndTime + ':00');

    if (startTime >= endTime) {
      setMessage('开始时间必须早于结束时间');
      return;
    }

    setUpdatingRecord(true);
    setMessage('');

    try {
      const fetchFunction = authenticatedFetch || fetch;
      const url = authenticatedFetch 
        ? `/api/me/activities/${recordId}` 
        : buildApiUrl(`/api/me/activities/${recordId}`);
      
      // 创建本地时间字符串，不进行时区转换
      const formatLocalDateTime = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };
      
      const res = await fetchFunction(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_time: formatLocalDateTime(startTime),
          end_time: formatLocalDateTime(endTime)
        })
      });

      if (res.ok) {
        const updatedRecord = await res.json();
        
        // 更新本地记录
        if (activityRecords.data) {
          setActivityRecords(prev => ({
            ...prev,
            data: prev.data?.map(record => 
              record.id === recordId 
                ? {
                    ...record,
                    start_time: updatedRecord.data.start_time,
                    end_time: updatedRecord.data.end_time,
                    duration_minutes: updatedRecord.data.duration_minutes
                  }
                : record
            ) || []
          }));
        }

        setMessage('活动记录更新成功！');
        setEditingRecordId(null);
        setEditingStartTime('');
        setEditingEndTime('');
        
        // 如果项目信息更新回调存在，调用它
        if (onProjectInfoUpdated) {
          onProjectInfoUpdated();
        }
      } else {
        const errorData = await res.json();
        setMessage(errorData.detail || '更新失败');
      }
    } catch (error) {
      console.error('更新活动记录失败:', error);
      setMessage('网络错误，请重试');
    } finally {
      setUpdatingRecord(false);
    }
  };

  // 加载更多记录
  const loadMoreRecords = useCallback(async () => {
    if (!hasMoreRecords || loadingMoreRecords || !userProject.data) {
      return;
    }
    
    setLoadingMoreRecords(true);
    await fetchActivityRecords(userProject.data.project_id, currentPage + 1, true);
    setLoadingMoreRecords(false);
  }, [hasMoreRecords, loadingMoreRecords, userProject.data, currentPage, fetchActivityRecords]);

  // 滚动加载更多
  const handleRecordsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    // 当滚动到距离底部100px时，加载更多
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreRecords();
    }
  }, [loadMoreRecords]);

  // 保存项目信息
  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    const hours = parseFloat(editData.target_hours) || 0;
    if (hours === 0) {
      setMessage('目标时长不能为0，请重新设置。');
      setSaving(false);
      return;
    }
    
    try {
      const fetchFunction = authenticatedFetch || fetch;
      const requestBody: any = {
        target_hours: hours,
        is_active: true
      };

      const url = authenticatedFetch ? `/api/me/projects/${userProjectId}` : buildApiUrl(`/api/me/projects/${userProjectId}`);
      const res = await fetchFunction(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (res.ok) {
        setMessage('保存成功！');
        // 更新本地数据
        if (userProject.data) {
          setUserProject(prev => ({
            ...prev,
            data: {
              ...prev.data!,
              ...requestBody
            }
          }));
        }
        setSaving(false);
        
        // 延迟1秒后自动返回首页
        setTimeout(() => {
          onBack();
        }, 1000);
      } else {
        const errorData = await res.json();
        setMessage(errorData.detail || '保存失败');
      }
    } catch (error) {
      setMessage('网络错误，请重试');
    } finally {
      setSaving(false);
    }
  };

  const [showTargetEdit, setShowTargetEdit] = useState(false);

  // 检查是否所有数据都在加载中
  const isAnyLoading = userProject.loading || projectInfo.loading || activityRecords.loading || calendarData.loading || weeklyAnalysis.loading;

  // 检查是否有任何数据加载失败
  const hasAnyError = userProject.error || projectInfo.error || activityRecords.error || calendarData.error || weeklyAnalysis.error;

  // ========== 独立渲染组件 ==========
  


  // 2. AI周报分析渲染组件
  const WeeklyAnalysisSection = () => {
    if (weeklyAnalysis.loading) {
      return (
        <div className="ai-analysis-section">
          <h3>📊 项目周报分析</h3>
          <div className="ai-analysis-content">
            <div className="ai-loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">
                <div>DeepSeek正在分析你的项目数据...</div>
                <div className="loading-subtitle">这可能需要十几秒的时间</div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (weeklyAnalysis.error) {
      return (
        <div className="ai-analysis-section">
          <h3>📊 项目周报分析</h3>
          <div className="ai-analysis-content">
            <div className="ai-error">
              <div className="error-icon">❌</div>
              <div className="error-text">分析失败: {weeklyAnalysis.error}</div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!weeklyAnalysis.data) {
      return (
        <div className="ai-analysis-section">
          <h3>📊 项目周报分析</h3>
          <div className="ai-analysis-content">
            <div className="ai-empty">
              <div className="empty-icon">📊</div>
              <div className="empty-text">暂无分析数据</div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="ai-analysis-section">
        <h3>📊 项目周报分析</h3>
        <div className="ai-analysis-content">
          <div className="ai-analysis-result">
            <div className="analysis-header">
              <div className="analysis-summary">
                <h4>📊 过去两周总结</h4>
                <p>{weeklyAnalysis.data.summary}</p>
              </div>
            </div>
            
            <div className="analysis-content">
              <div className="analysis-text">
                <ReactMarkdown>{weeklyAnalysis.data.analysis}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 3. 活动日历渲染组件
  const CalendarSection = () => {
    if (calendarData.loading) {
      return (
        <div className="calendar-section">
          <h3>项目时间分布</h3>
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <span>加载时间分布中...</span>
          </div>
        </div>
      );
    }
    
    if (calendarData.error) {
      return (
        <div className="calendar-section">
          <h3>项目时间分布</h3>
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <div className="error-text">加载失败: {calendarData.error}</div>
          </div>
        </div>
      );
    }
    
    if (!calendarData.data || calendarData.data.length === 0) {
      return (
        <div className="calendar-section">
          <h3>项目时间分布</h3>
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <div className="empty-text">暂无时间分布数据</div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="calendar-section">
        <h3>项目时间分布</h3>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {/* 左侧星期标签，显示完整的周几 */}
            <div style={{ display: 'flex', flexDirection: 'column', marginRight: 4, paddingTop: 13 }}>
              {WEEK_DAYS.map((d, i) => (
                <div
                  key={i}
                  style={{
                    height: 12.2, // 调整到平均值字高
                    fontSize: 9, // 调整到平均值字体大小
                    color: '#888',
                    lineHeight: '12.2px', // 调整到平均值行高
                    textAlign: 'right',
                    width: 14, // 调整到平均值宽度
                    boxSizing: 'border-box',
                    marginTop: i === 0 ? 0 : 2 // 调整到平均值间距
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
            {/* 主体内容区域 - 包含月份标签和格子，一起滚动 */}
            <div
              ref={calendarScrollRef}
              style={{ 
                overflowX: 'auto', 
                overflowY: 'hidden',
                maxWidth: '240px', // 使用固定宽度确保不超出容器
                WebkitOverflowScrolling: 'touch'
              }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 'max-content' }}>
                {/* 顶部月份标签 */}
                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 2 }}>
                  {monthLabels.map((label, idx) => (
                    <div key={idx} style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#888' }}>{label}</div>
                  ))}
                </div>
                {/* 主体格子 */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  {calendarWeeks.map((week, colIdx) => (
                    <div key={colIdx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {week.map((d, rowIdx) => (
                        <div
                          key={rowIdx}
                          className={isToday(d.date) ? 'calendar-day today' : 'calendar-day'}
                          style={getCalendarDayStyle(d, calendarMax, isDarkTheme, 8, 1)}
                          onClick={(e) => {
                            console.log('onClick 触发，日期:', d.date);
                            handleDayClick(d, e);
                          }}
                          onTouchStart={(e) => {
                            console.log('onTouchStart 触发，日期:', d.date);
                            // 添加触摸反馈
                            const target = e.currentTarget as HTMLElement;
                            target.style.transform = 'scale(0.95)';
                            target.style.boxShadow = '0 0 8px rgba(79, 209, 199, 0.3)';
                            
                            // 恢复原状
                            setTimeout(() => {
                              target.style.transform = '';
                              target.style.boxShadow = '';
                            }, 150);
                            
                            handleDayClick(d, e);
                          }}
                          title={d.date ? `${formatActivityDate(d.date)} - ${d.hours}小时` : ''}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 4. 活动记录渲染组件
  const ActivityRecordsSection = () => {
    if (activityRecords.loading) {
      return (
        <div className="records-section">
          <h3>活动记录</h3>
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <span>加载活动记录中...</span>
          </div>
        </div>
      );
    }
    
    if (activityRecords.error) {
      return (
        <div className="records-section">
          <h3>活动记录</h3>
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <div className="error-text">加载失败: {activityRecords.error}</div>
          </div>
        </div>
      );
    }
    
    if (!activityRecords.data || activityRecords.data.length === 0) {
      return (
        <div className="records-section">
          <h3>活动记录</h3>
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <div className="empty-text">暂无活动记录</div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="records-section">
        <h3>活动记录</h3>
        <div 
          ref={recordsScrollRef}
          className="records-list"
          onScroll={handleRecordsScroll}
          style={{ 
            maxHeight: '300px', 
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {activityRecords.data.map(record => (
            <div key={record.id} className="record-item">
              {editingRecordId === record.id ? (
                // 编辑模式
                <div className="record-edit">
                  <div className="edit-time-inputs">
                    <div className="time-input-group">
                      <label>开始时间:</label>
                      <input
                        type="datetime-local"
                        value={editingStartTime}
                        onChange={(e) => setEditingStartTime(e.target.value)}
                        className="time-input"
                      />
                    </div>
                    <div className="time-input-group">
                      <label>结束时间:</label>
                      <input
                        type="datetime-local"
                        value={editingEndTime}
                        onChange={(e) => setEditingEndTime(e.target.value)}
                        className="time-input"
                      />
                    </div>
                  </div>
                  <div className="edit-actions">
                    <button
                      className="save-btn"
                      onClick={() => handleSaveEdit(record.id)}
                      disabled={updatingRecord}
                    >
                      {updatingRecord ? '保存中...' : '保存'}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={updatingRecord}
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                // 显示模式
                <>
                  <div className="record-time">
                    <div>开始: {formatDateTime(record.start_time)}</div>
                    <div>结束: {formatDateTime(record.end_time)}</div>
                  </div>
                  <div className="record-duration">
                    {formatDuration(record.duration_minutes)}
                  </div>
                  <button
                    className="edit-record-btn"
                    onClick={() => handleStartEdit(record)}
                    title="编辑时间"
                  >
                    ✏️
                  </button>
                </>
              )}
            </div>
          ))}
          
          {/* 加载更多指示器 */}
          {loadingMoreRecords && (
            <div className="loading-more">
              <div className="loading-spinner"></div>
              <span>加载更多...</span>
            </div>
          )}
          
          {/* 没有更多数据提示 */}
          {!hasMoreRecords && activityRecords.data && activityRecords.data.length > 0 && (
            <div className="no-more-records">
              没有更多记录了
            </div>
          )}
        </div>
      </div>
    );
  };

  // 5. 项目编辑表单渲染组件
  const ProjectEditForm = () => {
    if (userProject.loading) {
      return (
        <div className="project-edit-form">
          <h3>项目设置</h3>
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            <span>加载项目设置中...</span>
          </div>
        </div>
      );
    }
    
    if (userProject.error) {
      return (
        <div className="project-edit-form">
          <h3>项目设置</h3>
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <div className="error-text">加载失败: {userProject.error}</div>
          </div>
        </div>
      );
    }
    
    if (!userProject.data) {
      return (
        <div className="project-edit-form">
          <h3>项目设置</h3>
          <div className="empty-state">
            <div className="empty-icon">⚙️</div>
            <div className="empty-text">暂无项目设置</div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="project-edit-form">
        <h3>项目设置</h3>
        <div className="form-group">
          <label>目标时长 (小时)</label>
          <button 
            className="save-project-btn" 
            onClick={handleSave} 
            disabled={saving}
          >
            {saving ? '保存中...' : '保存项目'}
          </button>
          <input
            type="text"
            value={editData.target_hours}
            onChange={(e) => setEditData({ target_hours: e.target.value })}
            className="form-input"
          />
        </div>
      </div>
    );
  };

  // ========== 主渲染逻辑 ==========
  
  // 移除全局加载状态检查，让每个区域独立渲染
  
  // 主渲染 - 每个模块独立渲染
  return (
    <>
      <div 
        className="project-edit"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          position: 'relative'
        }}
      >
        {/* 左滑提示遮罩 */}
        {swipeX > 20 && (
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(102, 126, 234, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            <div 
              style={{
                background: 'rgba(102, 126, 234, 0.9)',
                color: 'white',
                padding: '12px 20px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                transform: `translateX(${-swipeX * 0.5}px)`,
                opacity: Math.min(swipeX / 50, 1)
              }}
            >
              左滑回到首页
            </div>
          </div>
        )}
        
        <div 
          className="edit-header"
          style={{ height: `${headerHeight}px` }}
        >
          <button className="back-btn" onClick={onBack}>{'<'}</button>
          <span className="header-title">
            {projectInfo.data?.name || (userProject.loading ? '加载中...' : '项目详情')}
          </span>
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>

        <div className="edit-content">
          {/* AI周报分析 - 独立渲染 */}
          {WeeklyAnalysisSection()}
          
          {/* 活动日历 - 独立渲染 */}
          {CalendarSection()}
          
          {/* 活动记录 - 独立渲染 */}
          {ActivityRecordsSection()}
          
          {/* 项目编辑表单 - 独立渲染 */}
          {ProjectEditForm()}
        </div>
        

      </div>

      {/* 消息提示 - 移到最外层，不受其他容器影响 */}
      {message && (
        <>
          <div className="message-overlay" onClick={() => setMessage('')}></div>
          <div className="message">{message}</div>
        </>
      )}
      
      {/* Toast提示 */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '50vh',
            left: '50vw',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(51, 51, 51, 0.95)',
            color: '#fff',
            padding: '18px 28px',
            borderRadius: '16px',
            zIndex: 9999,
            fontSize: '16px',
            fontWeight: '500',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
            maxWidth: '90%',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
};

export default ProjectEdit; 